import { ReactNode } from "react";
import { STATUS_INDICATOR_VARIANTS } from "@cdkglobal/radial";
import { StatusIndicatorWithTooltip } from "../../../../common/Tooltip";
import {
  EvaluatedResults,
  IAMUserState,
  JsonSchema,
  RowData,
  RowDataValueType,
} from "../../../../../types";
import { ROW_DATA_KEYS, SCHEMA_TITLES } from "../../../constants";
import { resolveFieldDisplayValue } from "../../../helpers/setupGridFieldDisplayValueHelpers";
import { formatFieldValueToString } from "../../../helpers";

export type FieldRenderContext = {
  fieldName: string;
  fieldValue: RowDataValueType;
  rowData: RowData;
  setupName: string;
  schema: JsonSchema;
  iamUserState: IAMUserState;
  evaluatedResults: EvaluatedResults;
  highlightText: (text: string) => ReactNode;
};

type FieldRenderResolver = (ctx: FieldRenderContext) => ReactNode | undefined;

/** Accounts Receivable schedule code shows a status indicator when a switch status is present. */
const renderAccountsReceivableScheduleCode: FieldRenderResolver = (
  ctx: FieldRenderContext,
) => {
  const { setupName, fieldName, rowData, highlightText } = ctx;
  const value = resolveFieldDisplayValue(ctx);
  const displayValue = formatFieldValueToString(fieldName, value);

  if (
    setupName !== SCHEMA_TITLES.ACCOUNTS_RECEIVABLE ||
    fieldName !== ROW_DATA_KEYS.SCHEDULE_CODE ||
    !displayValue
  ) {
    return undefined;
  }

  const { [ROW_DATA_KEYS.AR_SWITCH_STATUS]: switchStatus } = rowData;

  if (!switchStatus) {
    return highlightText(displayValue);
  }

  return (
    <StatusIndicatorWithTooltip
      label={displayValue}
      variant={STATUS_INDICATOR_VARIANTS.INFORMATION}
      tooltip={displayValue}
    />
  );
};

/**
 * Ordered list of setup-specific field renderers. Add new entries here as
 * additional setups need custom field display logic.
 */
const CUSTOM_FIELD_RENDERERS: FieldRenderResolver[] = [
  renderAccountsReceivableScheduleCode,
];

export const resolveCustomFieldRender = (
  ctx: FieldRenderContext,
): ReactNode | undefined => {
  for (const renderer of CUSTOM_FIELD_RENDERERS) {
    const result = renderer(ctx);
    if (result !== undefined) return result;
  }
  return undefined;
};
